/**
 * useReceiptIssue フック
 *
 * 領収書発行（課題表381）の状態管理。
 * idle → submitting → issued / error の 4 状態を持ち、送信中の二重発行を防ぐ。
 */

import { useCallback, useRef, useState } from 'react';

import { appBridge, isWebViewEnvironment } from '../../../shared/services/bridge';
import { RECEIPT_ISSUE_TEXTS } from '../constants/receiptTexts';
import { paymentRepository } from '../data/paymentRepository';

export type ReceiptIssueStatus = 'idle' | 'submitting' | 'issued' | 'error';

interface UseReceiptIssueResult {
  status: ReceiptIssueStatus;
  addressName: string;
  setAddressName: (value: string) => void;
  /** 宛名が入力済みかつ送信中でなければ true */
  canSubmit: boolean;
  receiptUrl: string | null;
  errorMessage: string | null;
  /** 領収書を発行し、成功時に PDF を開く */
  submit: () => Promise<void>;
  /** 発行済みの領収書 PDF を再表示する */
  openReceipt: () => void;
}

/**
 * PDF を表示する。WebView ではアプリ内ビューア、Web 直接アクセスでは新しいタブ。
 * （PdfButton と同じ分岐。URL の http(s) 検証は appBridge.openPdfViewer 側で実施）
 */
const openPdf = (url: string): void => {
  if (isWebViewEnvironment()) {
    appBridge.openPdfViewer(url, RECEIPT_ISSUE_TEXTS.PDF_TITLE);
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

/**
 * @param inquiryId 問い合わせ ID
 * @param token 認証トークン
 */
export const useReceiptIssue = (inquiryId: string, token?: string): UseReceiptIssueResult => {
  const [status, setStatus] = useState<ReceiptIssueStatus>('idle');
  const [addressName, setAddressName] = useState('');
  const [receiptUrl, setReceiptUrl] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // 同一レンダー内の連打（disabled 反映前）でも API を 2 回呼ばないための同期ガード
  const isSubmittingRef = useRef(false);

  const trimmedAddressName = addressName.trim();
  const canSubmit =
    inquiryId !== '' && trimmedAddressName.length > 0 && status !== 'submitting';

  const submit = useCallback(async () => {
    if (!canSubmit || isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setStatus('submitting');
    setErrorMessage(null);
    // 兄弟画面（CashPaymentConfirm、PaymentConfirm、NP後払い）と同じく、送信中はネイティブの
    // 全画面ローディングを出す（PDF 生成は最大 60 秒）。WebView 外では postMessage が無視される
    appBridge.showLoading();

    try {
      const result = await paymentRepository.createReceipt(
        { inquiryId, addressName: trimmedAddressName },
        token,
      );

      if (!result.success) {
        appBridge.hideLoading();
        setStatus('error');
        setErrorMessage(RECEIPT_ISSUE_TEXTS.ERROR);
        return;
      }

      // PDF ビューア（ネイティブ画面）を開く前にローディングを解除する
      appBridge.hideLoading();
      setReceiptUrl(result.data.url);
      setStatus('issued');
      openPdf(result.data.url);
    } catch {
      // repository は ApiResult を返す設計だが、想定外の例外で submitting に固定されて
      // 復帰不能になるのを防ぐ（横断ルール: エラー時はローディング解除 + リトライ手段）
      appBridge.hideLoading();
      setStatus('error');
      setErrorMessage(RECEIPT_ISSUE_TEXTS.ERROR);
    } finally {
      isSubmittingRef.current = false;
    }
  }, [canSubmit, inquiryId, trimmedAddressName, token]);

  const openReceipt = useCallback(() => {
    if (receiptUrl) {
      openPdf(receiptUrl);
    }
  }, [receiptUrl]);

  return {
    status,
    addressName,
    setAddressName,
    canSubmit,
    receiptUrl,
    errorMessage,
    submit,
    openReceipt,
  };
};
